import { forwardRef, useEffect, useImperativeHandle } from 'react'
import { Col, Row } from 'react-bootstrap'
import moment from 'moment'
import { FormDatePicker, FormDropDownList } from 'components'
import { Field, FormRenderProps } from '@progress/kendo-react-form'
import { IDateSearchLocalStorageNames } from 'types'
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns'
import { getFromLocalStorage, saveToLocalStorage } from 'core/local-storage-helpers'
import { isValidYear } from 'core/helpers'
import { invalidDateValidator, MaxDateRangeValidator, startendValidator } from 'core/Validators'

export interface IFormDateSpanSelectorRef {
    reset: () => void;
}
interface IProps {
    formRenderProps: FormRenderProps
    localStorageNames?: IDateSearchLocalStorageNames
    onSearch: (startDate: Date, endDate: Date) => void
    className?: string
    minDate?: Date
    maxDate?: Date
    defaultRelativeDateOption?: RelativeDateOptionId
    includeRelativeDateOptions?: Array<RelativeDateOptionId>
    excludeRelativeDateOptions?: Array<RelativeDateOptionId>
    disabled?: boolean
}

export interface IRelativeDateOption {
    id: RelativeDateOptionId
    text: string
}

export enum RelativeDateOptionId {
    Custom = 'Custom',
    Today = 'Today',
    Yesterday = 'Yesterday',
    ThisWeek = 'ThisWeek',
    LastWeek = 'LastWeek',
    Last7Days = 'Last7Days',
    ThisMonth = 'ThisMonth',
    LastMonth = 'LastMonth',
    Last30Days = 'Last30Days',
    ThisQuarter = 'ThisQuarter',
    LastQuarter = 'LastQuarter',
    Last90Days = 'Last90Days',
    ThisYear = 'ThisYear',
    LastYear = 'LastYear',
    Last365Days = 'Last365Days'
}

export const relativeDateOptions: Array<IRelativeDateOption> = [
    { id: RelativeDateOptionId.Custom, text: 'Custom Date' },
    { id: RelativeDateOptionId.Today, text: 'Today' },
    { id: RelativeDateOptionId.Yesterday, text: 'Yesterday' },
    { id: RelativeDateOptionId.ThisWeek, text: 'This Week' },
    { id: RelativeDateOptionId.LastWeek, text: 'Last Week' },
    { id: RelativeDateOptionId.Last7Days, text: 'Last 7 Days' },
    { id: RelativeDateOptionId.ThisMonth, text: 'This Month' },
    { id: RelativeDateOptionId.LastMonth, text: 'Last Month' },
    { id: RelativeDateOptionId.Last30Days, text: 'Last 30 Days' },
    { id: RelativeDateOptionId.ThisQuarter, text: 'This Quarter' },
    { id: RelativeDateOptionId.LastQuarter, text: 'Last Quarter' },
    { id: RelativeDateOptionId.Last90Days, text: 'Last 90 Days' },
    { id: RelativeDateOptionId.ThisYear, text: 'This Year' },
    { id: RelativeDateOptionId.LastYear, text: 'Last Year' },
    { id: RelativeDateOptionId.Last365Days, text: 'Last 365 Days' }
]

export const getDateRangeForOption = (
    optionId: RelativeDateOptionId,
    minDate?: Date,
    maxDate?: Date
): { start: moment.Moment, end: moment.Moment } => {
    let start = moment()
    let end = moment()
    switch (optionId) {
        case RelativeDateOptionId.Today:
            start = moment()
            break
        case RelativeDateOptionId.Yesterday:
            start = moment().subtract(1, 'days')
            end = moment().subtract(1, 'days')
            break
        case RelativeDateOptionId.ThisWeek:
            start = moment().clone().startOf('week')
            break
        case RelativeDateOptionId.LastWeek:
            start = moment().clone().startOf('week').subtract(1, 'week')
            end = moment().clone().startOf('week').subtract(1, 'week').endOf('week')
            break
        case RelativeDateOptionId.Last7Days:
            start = moment().subtract(7, 'days')
            break
        case RelativeDateOptionId.ThisMonth:
            start = moment().clone().startOf('month')
            break
        case RelativeDateOptionId.LastMonth:
            start = moment().subtract(1, 'months').startOf('month')
            end = moment().clone().startOf('month').subtract(1, 'months').endOf('month')
            break
        case RelativeDateOptionId.Last30Days:
            start = moment().subtract(30, 'days')
            break
        case RelativeDateOptionId.ThisQuarter:
            start = moment().clone().startOf('quarter')
            break
        case RelativeDateOptionId.LastQuarter:
            start = moment().subtract(1, 'quarter').startOf('quarter')
            end = moment().clone().startOf('quarter').subtract(1, 'quarters').endOf('quarter')
            break
        case RelativeDateOptionId.Last90Days:
            start = moment().subtract(90, 'days')
            break
        case RelativeDateOptionId.ThisYear:
            start = moment().clone().startOf('year')
            end = moment().clone().endOf('year')
            break
        case RelativeDateOptionId.LastYear:
            start = moment().subtract(1, 'year').startOf('year')
            end = moment().clone().startOf('year').subtract(1, 'years').endOf('year')
            break
        case RelativeDateOptionId.Last365Days:
            start = moment().subtract(365, 'days')
            break
        case RelativeDateOptionId.Custom:
            start = moment(minDate)
            end = moment(maxDate)
            break
    }

    return { start: start.startOf('day'), end: end.endOf('day') }
}

export const filterRelativeDateOptions = (
    options: Array<IRelativeDateOption>,
    includeOptions?: Array<RelativeDateOptionId>,
    excludeOptions?: Array<RelativeDateOptionId>,
    minDate?: Date,
    maxDate?: Date): Array<IRelativeDateOption> => {
    if (includeOptions && excludeOptions) {
        throw new Error('Cannot include and exclude relative date options at the same time')
    }

    return options.filter(option => {
        // Always include the "Custom" option
        //if (option.id === RelativeDateOptionId.Custom) return true

        // Check if the option should be included
        if (includeOptions && !includeOptions.includes(option.id)) return false

        // Check if the option should be excluded
        if (excludeOptions && excludeOptions.includes(option.id)) return false

        const dateRange = getDateRangeForOption(option.id, minDate, maxDate)
        if (!dateRange) return true

        const { start, end } = dateRange

        const minMoment = minDate ? moment(minDate).startOf('day') : null
        const maxMoment = maxDate ? moment(maxDate).endOf('day') : null

        if (minMoment && start.isBefore(minMoment)) return false
        if (maxMoment && end.isAfter(maxMoment)) return false

        return true
    })
}

const FormDateSpanSelector = forwardRef<IFormDateSpanSelectorRef, IProps>(({
    formRenderProps,
    localStorageNames,
    onSearch,
    disabled,
    className = 'mb-lg-4 mb-sm-4 bg-light border rounded shadow-sm p-3',
    minDate,
    maxDate,
    defaultRelativeDateOption = RelativeDateOptionId.Last30Days,
    includeRelativeDateOptions,
    excludeRelativeDateOptions
}, ref) => {


    const getRelativeDateOptionFromLocalStorage = (): IRelativeDateOption | undefined => {
        if (!localStorageNames?.relativeDateOption) return undefined

        const option = getFromLocalStorage<IRelativeDateOption>(localStorageNames.relativeDateOption)
        if (!option) return undefined

        return relativeDateOptions.find(o => o.id === option.id)
    }

    const getDateFromLocalStorage = (key: string): Date | undefined => {
        if (!localStorageNames) return undefined

        const storedDate = getFromLocalStorage<string>(key)
        return storedDate ? moment(storedDate).toDate() : undefined
    }

    //filter out options that are not within the min and max date range
    const filteredOptions = filterRelativeDateOptions(relativeDateOptions, includeRelativeDateOptions, excludeRelativeDateOptions, minDate, maxDate)
    const storedRelativeDateOption = getRelativeDateOptionFromLocalStorage()

    const setRelativeDateOption = (filteredArray: IRelativeDateOption[], storedOption: IRelativeDateOption | undefined) => {
        if (storedOption) {
            const stored = filteredArray.find(o => o.id === storedOption.id)
            if (stored) {
                return stored
            }
        }
        const defaultOption = filteredArray.find(o => o.id === defaultRelativeDateOption)
        if (defaultOption) {
            return defaultOption
        }
        const componantDefault = filteredOptions.find(o => o.id === RelativeDateOptionId.Last30Days)
        if (componantDefault) {
            return componantDefault
        }

        if (filteredOptions.length > 1) {
            return filteredOptions[1]
        }
        return filteredOptions[0]
    }

    //look up stored option
    //if not found, look up default option passed as prop
    //if not found, look up default option, which is 30 days.
    //if not found, look up first option after custom
    const intitalRelativeDateOption: IRelativeDateOption = setRelativeDateOption(filteredOptions, storedRelativeDateOption)

    // This logic looks sound, but for some reason was always returning filteredOptions[0] if defaultRelativeDateOption was set
    // (storedRelativeDateOption && filteredOptions.find(o => o.id === storedRelativeDateOption.id))
    //     ?? filteredOptions.find(o => o.text === defaultRelativeDateOption)
    //     ?? filteredOptions.find(o => o.id === RelativeDateOptionId.Last30Days)
    //     ?? filteredOptions.length > 1 ? filteredOptions[1] : filteredOptions[0]

    const initialDateRange = getDateRangeForOption(intitalRelativeDateOption.id, minDate, maxDate)
    const storedStartDate = intitalRelativeDateOption.id === RelativeDateOptionId.Custom
        ? getDateFromLocalStorage(localStorageNames?.startDate || '')
        : undefined
    const storedEndDate = intitalRelativeDateOption.id === RelativeDateOptionId.Custom
        ? getDateFromLocalStorage(localStorageNames?.endDate || '')
        : undefined

     const initalStartDate =
   intitalRelativeDateOption.id === RelativeDateOptionId.Custom && ((storedStartDate && !minDate) || 
     (storedStartDate && moment(storedStartDate).isBetween(initialDateRange.start, initialDateRange.end, undefined, '[]')))
            ? storedStartDate
            : initialDateRange.start.toDate()
          
      const initalEndDate =
     intitalRelativeDateOption.id === RelativeDateOptionId.Custom && ((storedEndDate && !minDate) || 
     (storedEndDate && moment(storedEndDate).isBetween(initialDateRange.start, initialDateRange.end, undefined, '[]')))
         ? storedEndDate
        : initialDateRange.end.toDate()

    useEffect(() => {
        formRenderProps.onChange('relativeDateOption', { value: intitalRelativeDateOption })
        formRenderProps.onChange('startDate', { value: initalStartDate })
        formRenderProps.onChange('endDate', { value: initalEndDate })
    }, [intitalRelativeDateOption])

    useImperativeHandle(ref, () => ({
        reset: () => {
            let option = filteredOptions.find(o => o.id === defaultRelativeDateOption) ?? { text: 'Last 30 Days', id: RelativeDateOptionId.Last30Days }
            const dateRange = getDateRangeForOption(option.id, minDate, maxDate)
            formRenderProps.onChange('relativeDateOption', { value: option })

            let startDate = dateRange.start.startOf('day').toDate()
            let endDate = dateRange.end.endOf('day').toDate()

            formRenderProps.onChange('startDate', { value: startDate })
            formRenderProps.onChange('endDate', { value: endDate })

            saveToLocalStorage(localStorageNames?.relativeDateOption, option)
            if (option.id === RelativeDateOptionId.Custom) {
                saveToLocalStorage(localStorageNames?.startDate, startDate)
                saveToLocalStorage(localStorageNames?.endDate, endDate)
            }
        }
    }))

    const onRelativeDateOpenChange = (e: DropDownListChangeEvent) => {
        if (!checkValidDateSpan()) {
            const option = e.target.value as IRelativeDateOption
            saveToLocalStorage(localStorageNames?.relativeDateOption, option)
            const dateRange = getDateRangeForOption(option.id, minDate, maxDate)
            if (!dateRange)
                return

            let startDate = dateRange.start.startOf('day').toDate()
            let endDate = dateRange.end.endOf('day').toDate()
            if (option.id === RelativeDateOptionId.Custom) {
             startDate = formRenderProps.valueGetter('startDate')
             endDate = formRenderProps.valueGetter('endDate')       
            saveToLocalStorage(localStorageNames?.startDate, startDate)
            saveToLocalStorage(localStorageNames?.endDate, endDate)
            }

            formRenderProps.onChange('startDate', { value: startDate })
            formRenderProps.onChange('endDate', { value: endDate })

            if (startDate && endDate) {
                onSearch(startDate, endDate)
            }
        }
    }    

    const onStartDateChange = (e: DropDownListChangeEvent) => {
        if (!checkValidDateSpan()) {
           const relativeOption = formRenderProps.valueGetter('relativeDateOption')
            if (relativeOption?.id === RelativeDateOptionId.Custom) {
                saveToLocalStorage(localStorageNames?.startDate, e.target.value)
            }
            const startDate = formRenderProps.valueGetter('startDate')
            const endDate = formRenderProps.valueGetter('endDate')

            if (startDate && endDate) {
                onSearch(startDate, endDate)
            }
        }
    }

    const onEndDateChange = (e: DropDownListChangeEvent) => {
        if (!checkValidDateSpan()) {
           const relativeOption = formRenderProps.valueGetter('relativeDateOption')
            if (relativeOption?.id === RelativeDateOptionId.Custom) {
                saveToLocalStorage(localStorageNames?.endDate, e.target.value)
            }
            const startDate = formRenderProps.valueGetter('startDate')
            const endDate = formRenderProps.valueGetter('endDate')

            if (startDate && endDate) {
                onSearch(startDate, endDate)
            }
        }
    }

    const checkValidDateSpan = () => {
        const startDate = formRenderProps.valueGetter('startDate')
        const endDate = formRenderProps.valueGetter('endDate')

        if (startDate && endDate && isValidYear(startDate) && isValidYear(endDate)) {
            const utcStartDate = moment(startDate).utc().toISOString()
            const utcEndDate = moment(endDate).utc().toISOString()

            if (maxDate) {
                return MaxDateRangeValidator({ startDateInput: utcStartDate, endDateInput: utcEndDate, maxRangeInput: maxDate.toString() })
            } else {
                return startendValidator({ startDateInput: utcStartDate, endDateInput: utcEndDate })
            }
        }

        return invalidDateValidator(startDate, endDate)
    }

    return (
        <div className={className}>
            <Row className='mb-3'>
                <Col lg={4}>
                    <Field
                        label='Filter By Date:'
                        name='relativeDateOption'
                        textField='text'
                        dataItemKey='id'
                        data={filteredOptions}
                        component={FormDropDownList}
                        onChange={onRelativeDateOpenChange}
                        disabled={disabled}
                    />
                </Col>

                <Col lg={4}>
                    <Field
                        name='startDate'
                        label='Start Date:'
                        disabled={disabled || formRenderProps.valueGetter('relativeDateOption')?.id !== RelativeDateOptionId.Custom}
                        component={FormDatePicker}
                        min={minDate}
                        max={formRenderProps.valueGetter('endDate')}
                        onChange={onStartDateChange}
                    />
                </Col>

                <Col lg={4}>
                    <Field
                        name='endDate'
                        label='End Date:'
                        disabled={disabled || formRenderProps.valueGetter('relativeDateOption')?.id !== RelativeDateOptionId.Custom}
                        component={FormDatePicker}
                        max={maxDate}
                        onChange={onEndDateChange}
                    />
                </Col>
            </Row>
        </div>
    )
})

export default FormDateSpanSelector
